import {
  CaretUpDownIcon,
  CheckIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { toast } from 'sonner';
import colors from 'tailwindcss/colors';
import { useDebouncedCallback } from 'use-debounce';

import { Input } from '@/components/ui/input';

interface EventNumberFieldProps {
  defaultValue: string;
  maxValue?: number;
  onChange: (value: number) => void;
}

export function EventNumberField({
  defaultValue,
  onChange,
  maxValue = 1000,
}: EventNumberFieldProps) {
  const [value, setValue] = useState(defaultValue);
  const [isFieldActive, setIsFieldActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const validateInput = (inputValue: string): string | null => {
    const numValue = Number(inputValue);

    if (isNaN(numValue)) {
      return 'Please enter a valid number';
    }

    if (numValue < 0) {
      return 'Value cannot be negative';
    }

    if (numValue > maxValue) {
      return `Value cannot exceed ${maxValue}`;
    }

    return null;
  };

  const handleUpdate = async () => {
    if (
      value?.trim()?.length &&
      value !== defaultValue &&
      !isUpdating &&
      !error
    ) {
      setIsUpdating(true);
      try {
        onChange(Number(value));

        setIsUpdating(false);
      } catch (error) {
        toast.error('Uh oh! Something went wrong.');
      } finally {
        setIsUpdating(false);
        setIsFieldActive(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (newValue === '') {
      setError(null);
      return;
    }

    const validationError = validateInput(newValue);
    setError(validationError);
  };

  const handleBlur = useDebouncedCallback(() => {
    if (error) {
      // If there's an error, revert to the last valid value or default
      setError(null);
    }

    if (!isUpdating) {
      setValue(defaultValue);
    }
    setIsFieldActive(false);
  }, 600);

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      if (value === defaultValue) {
        handleBlur();
      } else {
        await handleUpdate();
      }
    }
    if (event.key === 'Escape') {
      setValue(defaultValue);
      setError(null);
      setIsFieldActive(false);
    }
  };

  if (isFieldActive) {
    return (
      <div className="relative w-[140px]">
        <Input
          autoFocus
          value={value}
          onBlur={handleBlur}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={`w-[140px] border-0 ${error ? 'border-red-500 focus:border-red-500' : ''}`}
        />

        {!error && value?.trim()?.length > 0 && (
          <div
            className="absolute top-2 right-1 cursor-pointer"
            onClick={handleUpdate}
          >
            {isUpdating ? (
              <CircleNotchIcon size={24} className="animate-spin" />
            ) : (
              <CheckIcon color={colors.green[400]} size={24} />
            )}
          </div>
        )}
        {error && (
          <div className="pt-2 text-xs text-red-500 whitespace-nowrap">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="line-clamp-1 cursor-pointer flex items-center justify-between gap-1 w-[140px]"
      onClick={() => setIsFieldActive(true)}
    >
      <span>{value || '0'}</span>
      <CaretUpDownIcon size={16} />
    </div>
  );
}
